import React from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';
import { FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import './DeleteConfirmationModal.css';

const DeleteConfirmationModal = ({
  show,
  onHide,
  onConfirm,
  title = 'Confirm Delete',
  message = 'Are you sure you want to delete this item?',
  itemName = '',
  confirmText = 'Delete',
  cancelText = 'Cancel',
  variant = 'danger', // danger, warning
  // When set, show result in modal (success or error) with only Close button
  resultMessage = null,
  resultVariant = 'danger', // 'success' | 'danger'
  isDeleting = false, // show loader and disable buttons until response
  // Optional: show checkbox to also delete related (child) records
  showDeleteChildrenCheckbox = false,
  deleteChildrenChecked = false,
  onDeleteChildrenCheckboxChange = () => {},
}) => {
  const handleConfirm = async () => {
    const result = onConfirm();
    if (result && typeof result.then === 'function') {
      await result;
    }
    // Parent controls closing (e.g. after showing result in modal)
  };

  const getIconColor = () => {
    if (variant === 'warning') {
      return getComputedStyle(document.documentElement).getPropertyValue('--color-primary').trim() || '#243A73';
    }
    return '#dc3545';
  };

  const showingResult = resultMessage != null && resultMessage.trim() !== '';
  const showingLoader = isDeleting && !showingResult;

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      className="delete-confirmation-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {showingResult ? (
            resultVariant === 'success' ? (
              <><FaCheckCircle className="me-2 text-success" /> Result</>
            ) : (
              <><FaExclamationTriangle className="me-2" style={{ color: '#dc3545' }} /> Error</>
            )
          ) : (
            <><FaExclamationTriangle className="me-2" style={{ color: getIconColor() }} /> {title}</>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {showingResult ? (
          <Alert variant={resultVariant} className="mb-0">
            {resultMessage}
          </Alert>
        ) : showingLoader ? (
          <div className="d-flex align-items-center gap-2">
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            <span>Deleting...</span>
          </div>
        ) : (
          <>
            <p className="mb-0">
              {message}
              {itemName && (
                <strong className="d-block mt-2 text-dark">{itemName}</strong>
              )}
            </p>
            {showDeleteChildrenCheckbox && (
              <div className="form-check mt-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="delete-children-checkbox"
                  checked={deleteChildrenChecked}
                  onChange={e => onDeleteChildrenCheckboxChange(e.target.checked)}
                  disabled={isDeleting}
                />
                <label className="form-check-label" htmlFor="delete-children-checkbox">
                  Also delete related (child) records
                </label>
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {showingResult ? (
          <Button variant="primary" onClick={onHide}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onHide} disabled={isDeleting}>
              {cancelText}
            </Button>
            <Button variant={variant} onClick={handleConfirm} disabled={isDeleting}>
              {isDeleting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Deleting...
                </>
              ) : (
                confirmText
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default DeleteConfirmationModal;
